import type {ReactNode, CSSProperties, ReactElement} from "react";
import {useState, useRef, useEffect, useMemo, cloneElement, forwardRef} from "react";
import type {colorThemes} from "@cdx/ds/css/themes/themes.css";
import {css} from "@cdx/ds";
import cx from "../cx";
import Portal from "../Portal";
import {springConfigs, useReveal} from "../hooks/useReveal";
import {delayedTrigger} from "../delayed-trigger";
import {ArrowOverlay, OverlayPlacer as OriginalOverlayPlacer} from ".";

const OverlayPlacer = OriginalOverlayPlacer;

const checkIfRef = (obj: any) =>
  obj !== null && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "current");

export const useGetNodeRefFromRef = (maybeRef: any) => {
  const innerRef = useRef();
  const isRef = checkIfRef(maybeRef);

  return {
    nodeRef: isRef ? maybeRef : innerRef,
    ref: useMemo(() => {
      if (isRef) return maybeRef;
      if (typeof maybeRef === "function") {
        return (nextNode: any) => {
          innerRef.current = nextNode;
          maybeRef(nextNode);
        };
      }
      return innerRef;
    }, [isRef, maybeRef]),
  };
};

export const useGetNodeFromRef = (maybeRef: any) => {
  const [innerNode, setInnerNode] = useState(null);
  const isRef = checkIfRef(maybeRef);
  const node = isRef ? maybeRef.current : innerNode;

  useEffect(() => {
    if (isRef) {
      setInnerNode(() => maybeRef.current || null);
      return () => setInnerNode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRef]);

  return {
    node,
    ref: useMemo(() => {
      if (isRef) return maybeRef;
      if (typeof maybeRef === "function")
        return (nextNode: any) => {
          setInnerNode(nextNode);
          maybeRef(nextNode);
        };
      return setInnerNode;
    }, [isRef, maybeRef]),
  };
};

// if I waited for a delayed tooltip, all other delayed tooltips should show up immediately!
let isInReadingMood = false;
const killReadingMoodTrigger = delayedTrigger();

type TooltipContent = string | ReactNode | (() => ReactNode);
export type TooltipOptions = Partial<{
  hidden: boolean;
  forceOpen: boolean;
  delayed: boolean;
  delayMs: number;
  className: string;
  placement: "top" | "left" | "bottom" | "right";
  colorTheme: keyof typeof colorThemes;
  targetIsDisabled: boolean;
  distanceFromAnchor: number;
  style: CSSProperties;
  outerClassName: string;
  onMouseEnter: React.MouseEventHandler;
  onMouseLeave: React.MouseEventHandler;
}>;

export const useTooltip = (
  props: TooltipOptions & {node: HTMLElement; content: TooltipContent}
) => {
  const {
    node,
    hidden,
    forceOpen,
    delayed,
    content,
    className,
    placement = "top",
    colorTheme = "active600",
    targetIsDisabled,
    distanceFromAnchor = 5,
    style: userStyle = {},
    outerClassName,
  } = props;

  const [open, setOpen] = useState(false);
  const propsRef = useRef(props);
  propsRef.current = props;

  const eventsRef = useRef<any>();
  if (!eventsRef.current) {
    const trigger = delayedTrigger();
    const killIt = () => {
      trigger.cancel();
      setOpen(false);
      killReadingMoodTrigger.fire(() => {
        isInReadingMood = false;
      }, 500);
    };
    eventsRef.current = {
      events: {
        onMouseEnter: (e: React.MouseEvent) => {
          propsRef.current.onMouseEnter?.(e);
          if (isInReadingMood) {
            setOpen(true);
            killReadingMoodTrigger.cancel();
          } else {
            trigger.fire(
              () => {
                setOpen(true);
                if (delayed) isInReadingMood = true;
              },
              propsRef.current.delayMs ?? propsRef.current.delayed ? 500 : 50
            );
          }
        },
        onMouseLeave: (e: React.MouseEvent) => {
          propsRef.current.onMouseLeave?.(e);
          killIt();
        },
      },
    };
  }

  const shownContent = !hidden && (open || forceOpen) && content;

  useEffect(() => {
    if (open) {
      const handleMouseMove = (e: MouseEvent) => {
        if (!propsRef.current.node) return;
        const isIn = propsRef.current.node.contains(e.target as Node);
        if (!isIn) eventsRef.current.events.onMouseLeave();
      };
      document.addEventListener("mousemove", handleMouseMove);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
      };
    }
  }, [open]);

  useEffect(() => {
    if (targetIsDisabled) {
      const handleMouseMove = (e: MouseEvent) => {
        if (!propsRef.current.node) return;
        const isIn = propsRef.current.node.contains(e.target as Node);
        if (!open && isIn) {
          eventsRef.current.events.onMouseEnter();
        } else if (open && !isIn) {
          eventsRef.current.events.onMouseLeave();
        }
      };
      // FIXME: this doesn't work if the node is in a modal which blocks mousemove events
      document.addEventListener("mousemove", handleMouseMove);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
      };
    }
  }, [targetIsDisabled, open]);

  const reveal = useReveal(shownContent, {config: springConfigs.quick} as any);
  const tooltip = reveal((revealProps: any, revealContent: any) => {
    const realContent = typeof revealContent === "function" ? revealContent() : revealContent;
    const isString = typeof realContent === "string";
    const renderOverlay = (overProps: any) => (
      <ArrowOverlay
        {...overProps}
        colorTheme={colorTheme}
        arrowSize="xs"
        contentStyle={{
          ...(isString && {
            maxWidth: Math.min(275, (userStyle.maxWidth as number) || Infinity),
            textAlign: "center",
          }),
          ...userStyle,
        }}
        noPointerEvents
        className={css(
          {
            textType: "prose12",
            color: "primary",
            pa: isString ? "8px" : undefined,
          },
          className
        )}
        outerClassName={outerClassName}
      >
        {realContent}
      </ArrowOverlay>
    );
    return (
      <Portal>
        <OverlayPlacer
          isOpen
          distanceFromAnchor={distanceFromAnchor}
          node={node}
          presenceProps={revealProps}
          renderOverlay={renderOverlay}
          placement={placement}
        />
      </Portal>
    );
  });

  return {events: eventsRef.current.events, tooltipElement: tooltip};
};

export const TooltipForChild = forwardRef<
  HTMLElement,
  {
    tooltip: TooltipContent;
    children: ReactElement;
    className?: string;
    tooltipClassName?: TooltipOptions["className"];
  } & Omit<TooltipOptions, "className">
>(({tooltip, children, className, tooltipClassName, ...options}, passedRef) => {
  const {
    hidden,
    forceOpen,
    delayed,
    delayMs,
    placement,
    targetIsDisabled,
    distanceFromAnchor,
    style,
    colorTheme,
    outerClassName,
    onMouseEnter,
    onMouseLeave,
    ...rest
  } = options;
  const {node, ref} = useGetNodeFromRef(passedRef);
  const {events, tooltipElement} = useTooltip({
    node,
    content: tooltip,
    hidden,
    forceOpen,
    delayed,
    delayMs,
    placement,
    colorTheme,
    targetIsDisabled,
    distanceFromAnchor,
    style,
    outerClassName,
    className: tooltipClassName,
    onMouseEnter,
    onMouseLeave,
  });
  if (process.env.NODE_ENV !== "production") {
    if (children) {
      if (!children.props) {
        console.info({children, tooltip});
        throw new Error("Do not pass non-componets as children of <TooltipForChild/>!");
      }
      if ("ref" in children.props) {
        throw new Error(
          "Do not pass refs to children of <TooltipForChild/>. Pass it to the Tooltip directly"
        );
      }
    }
  }
  return (
    <>
      {tooltipElement}
      {cloneElement(children, {
        ...events,
        ref,
        ...(className && {className: cx(className, children.props.className)}),
        ...rest,
      })}
    </>
  );
});
